import { useFetchWrapper, useRedirect } from "@core/composables"
import { Building, BuildingSource, Freq, WorkflowEnedis } from "@types"
import { defineStore } from "pinia"
import { computed, inject, ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { formatNumber, formatDateFr } from "@core/helpers"
import { getSourceConfigByAmount } from "@modules/energy-manager/helpers"
import moment from "moment"
import "moment/dist/locale/fr"

import { useI18n } from "vue-i18n"
import { VARIABLES } from "@modules/energy-manager/constants"
import { ServiceError } from "@core/exceptions"
import { GetCompareGraphByBuildingIdsResponse, Services } from "@services"

/**
 * Délai maximal d'attente du résultat d'une comparaison en tâche de fond (DEV-2199).
 * Au-delà, la carte passe en erreur `calcul-trop-long` au lieu de tourner sans fin ;
 * un résultat arrivé ensuite est ignoré (la tâche est retirée du store).
 */
export const DELAI_MAX_COMPARAISON_MS = 5 * 60 * 1000

export const useEmConsoCompareStore = defineStore("emConsoCompare", () => {
    // =========================================
    // Services
    // =========================================
    const authStore = useAuthStore()
    const taskStore = useTaskStore()
    const { redirectWrapper } = useRedirect()
    const { t } = useI18n()
    const {
        $getBuildingsByCompanyId,
        $getCompareGraphByBuildingIds,
        $exportAllDataByCompanyId,
        $exportRT,
        $createRapport,
        // Tags
        $getTags,
        $createBuildingTag,
        $deleteCompanyTag,
        $addCompanyTag,
        $deleteBuildingTag,
        $getFolderTree,
    } = inject<Services>("services")!

    // =========================================
    // Variables
    // =========================================
    /**
     * Liste des dossiers de l'organisation
     */
    const folders = ref<any>([])

    /**
     * Choix de la comparaison
     * 1 - Tous les bâtiments de l'organisation
     * 2 - Un dossier ou plusieurs dossiers
     * 3 - Un bâtiment ou plusieurs bâtiments
     */
    const compareChoice = ref<"all" | "folder" | "building">("all")

    /**
     * Liste des identifiants des bâtiments à comparer
     */
    const compareListId = ref<string[]>([])

    /**
     * Liste des identifiants de dossiers à comparer
     */
    const compareListIdByFolder = ref<string[]>([])

    /**
     * Fréquence de comparaison
     */
    const compareFreq = ref<Freq>("daily")

    /**
     * Période de comparaison
     */
    const comparePeriod = ref<[Date | null, Date | null]>([
        new Date(),
        new Date(),
    ])
    /**
     * Période de comparaison avec une autre période
     */
    const comparePeriodWith = ref<[Date | null, Date | null]>([null, null])

    /**
     * Liste des bâtiments de l'organisation en détail
     */
    const buildings = ref<Building[]>([])

    /**
     * Liste des workflows Enedis de l'organisation
     */
    const workflowEnedis = ref<WorkflowEnedis[]>([])

    /**
     * Timestamp d'expiration du cache bâtiments/dossiers (pour FolderDialog).
     * Évite de refetch à chaque ouverture de la modal (TTL 10 min).
     */
    const buildingsAndFoldersCacheExpiresAt = ref<number>(0)

    /**
     * Liste des tags des bâtiments de l'organisation
     */
    const tags = ref<string[]>([])

    /**
     * Variable de comparaison utilisée
     */
    const compareVariable = ref<string | undefined>("energie_tout_tout.sum")

    /**
     * Données de comparaison
     */
    const compareData = ref<Record<string, number>[]>([])

    /**
     * Graphique de comparaison
     */
    const compareGraphData = ref<any>(null)

    /**
     * Indicateurs de comparaison par période
     */
    const comparePeriodIndicators = ref<
        {
            date_from: string
            date_to: string
            value: number
        }[]
    >([])

    /**
     * Enregistre temporairement les données de comparaison pour les messages d'erreurs
     */
    const snapshotCompareBody = ref<{
        variable: string
        periods: string[]
        freq: Freq
        unit: string
        _id_buildings: string[]
    } | null>(null)

    // =========================================
    // Computed
    // =========================================
    /**
     * Récupère les dossiers de l'organisation par rapport aux bâtiments de l'organisation
     */
    const getFoldersList = computed(() => {
        // Regroupe les bâtiments par dossier
        const folders = buildings.value.reduce(
            (acc, building) => {
                if (!building.folder) {
                    acc[""] = acc[""] || {
                        _id_folder: "",
                        name: "Sans dossier",
                        parent: null,
                        buildings: [],
                        sources: [],
                    }

                    acc[""].buildings.push(building)
                    return acc
                }

                if (!acc[building.folder._id_folder]) {
                    acc[building.folder?._id_folder] = {
                        _id_folder: building.folder._id_folder,
                        name: building.folder.name,
                        parent: building.folder?.parent,
                        buildings: [],
                        sources: [],
                    }
                }
                acc[building.folder._id_folder].buildings.push(building)

                return acc
            },
            {} as Record<
                string,
                {
                    _id_folder: string
                    name: string
                    parent?: {
                        _id_folder: string
                        name: string
                    }
                    buildings: Building[]
                    sources: {
                        _id_source: string
                        segment?: string
                        idsource: string
                        idtype: string
                    }[]
                }
            >
        )

        // calcul pour chaque dossiers le nombre de sources au total
        Object.values(folders).forEach((folder) => {
            folder.sources = folder.buildings.flatMap((building) =>
                building.sources
                    .map((source) => ({
                        _id_source: source._id_source,
                        segment: source.segment,
                        idtype: source.idtype,
                        idsource: source.idsource,
                    }))
                    // retire les doublons
                    .filter(
                        (source, index, self) =>
                            self.findIndex(
                                (s) => s._id_source === source._id_source
                            ) === index
                    )
            )
        })

        return Object.values(folders)
    })

    /**
     * Transforme la liste des dossiers en arbre
     */
    const folderTreeList = computed(() => {
        const enrichFolder = (folder: any) => {
            const newFolder = {
                ...folder,
                children: folder.children ? [...folder.children] : [],
            }
            newFolder.buildings = buildings.value.filter(
                (building) =>
                    building.folder &&
                    building.folder._id_folder === newFolder._id
            )
            newFolder.sources = newFolder.buildings
                .flatMap((building: { sources: any }) => building.sources)
                .filter(
                    (source: { _id_source: any }, index: any, self: any[]) =>
                        self.findIndex(
                            (s) => s._id_source === source._id_source
                        ) === index
                )
            if (newFolder.children.length) {
                newFolder.children = newFolder.children.map(enrichFolder)
            }
            return newFolder
        }

        return folders.value.map(enrichFolder)
    })

    /**
     * Récupère la liste filtrée des bâtiments à comparer
     */
    const getComparedBuildingsList = computed(() => {
        // dans le cas où on compare tous les bâtiments, on retourne la liste des bâtiments
        if (compareChoice.value === "all") {
            return buildings.value
        }
        // sinon on filtre selon les identifiants des bâtiments à comparer
        return buildings.value.filter((building) =>
            compareListId.value.includes(building._id_building)
        )
    })

    /**
     * Récupère la liste filtrée des dossiers à comparer
     */
    const getComparedFoldersList = computed(() => {
        return getFoldersList.value.filter((folder: { _id_folder: string }) =>
            compareListIdByFolder.value.includes(folder._id_folder)
        )
    })

    /**
     * Récupère le nombre de sources à comparer au total sans les stations météos
     */
    const getSourcesCount = computed(() => {
        return (
            getComparedBuildingsList.value
                .flatMap((building) =>
                    building.sources.map((source: BuildingSource) => {
                        return {
                            _id_source: source._id_source,
                            idtype: source.idtype,
                        }
                    })
                )
                // retire les doublons
                .filter(
                    (source, index, self) =>
                        self.findIndex(
                            (s) => s._id_source === source._id_source
                        ) === index
                )
                // filtre les stations météos
                .filter(
                    (source) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                )?.length
        )
    })

    /**
     * Récupère la configuration d'affichage des variables en fonction du nombre de sources
     */
    const getDisplayDataConfig = computed(() => {
        return getSourceConfigByAmount(getSourcesCount.value ?? 0)
    })

    // =========================================
    // Actions
    // =========================================
    /** TTL du cache bâtiments/dossiers (10 min) pour FolderDialog */
    const BUILDINGS_FOLDERS_CACHE_TTL_MS = 10 * 60 * 1000

    /**
     * Récupère la liste des bâtiments et l'arbre des dossiers
     * puis met à jour le store. Utilise un cache de 10 min pour éviter les refetch à chaque ouverture de la modal.
     */
    const fetchBuildingsAndFolders = useFetchWrapper(async () => {
        if (Date.now() < buildingsAndFoldersCacheExpiresAt.value) {
            return
        }
        const [folderRes, buildingRes] = await Promise.all([
            // Récupération de l'arbre des dossiers
            $getFolderTree(),
            // Récupération des bâtiments
            $getBuildingsByCompanyId(authStore.companyId, {
                eventBusName: "update:buildings",
            }),
        ])
        folders.value = folderRes.data ?? folderRes
        buildings.value = buildingRes.data

        compareListId.value = buildingRes.data.map(
            (building: Building) => building._id_building
        )
        compareChoice.value = "all"
        buildingsAndFoldersCacheExpiresAt.value =
            Date.now() + BUILDINGS_FOLDERS_CACHE_TTL_MS
    })

    /**
     * Charge bâtiments et dossiers pour la modal FolderDialog si le cache (10 min) est expiré.
     * À appeler à l'ouverture de la modal pour éviter un refetch à chaque fois.
     */
    function ensureBuildingsAndFoldersForDialog(): Promise<void> {
        if (Date.now() < buildingsAndFoldersCacheExpiresAt.value) {
            return Promise.resolve()
        }
        return fetchBuildingsAndFolders.fetch()
    }

    /**
     * Quand on clique sur un lien, redirige via le router
     * @param id - identifiant de la source, du bâtiment ou du dossier
     * @param type - type de lien (source, building, folder)
     * @param _blank - ouvrir dans un nouvel onglet (non géré par redirectWrapper, conservé pour compat postMessage)
     */
    function clickLink(id: string, type: string, _blank?: boolean) {
        switch (type) {
            case "source":
                redirectWrapper("/source/:_id_source", { _id_source: id })
                break
            case "building":
                redirectWrapper("/building/:_id_building", {
                    _id_building: id,
                })
                break
            case "folder":
                redirectWrapper("/folder/:_id_folder", { _id_folder: id })
                break
        }
    }
    /**
     * Mettre à jour la liste des bâtiments
     * @param newBuildings
     */
    const setBuildings = (newBuildings: Building[]) => {
        buildings.value = newBuildings
    }

    /**
     * Restreint le comparateur de consommations à un seul bâtiment (page bâtiment « Évolution de la conso »).
     * @param building - Bâtiment courant (même forme que les entrées `$getBuildingsByCompanyId`)
     */
    function hydrateCompareScopeForBuilding(building: Building) {
        compareChoice.value = "building"
        compareListIdByFolder.value = []
        const id = building._id_building ?? building._id
        compareListId.value = id ? [id] : []
        buildings.value = [building]
    }
    /**
     * Récupère les bâtiments de l'organisation
     */
    const fetchBuildingsByCompanyId = useFetchWrapper(async () => {
        const { data } = await $getBuildingsByCompanyId(authStore.companyId)

        buildings.value = data

        // ajoute les identifiants des bâtiments dans la liste de comparaison
        compareListId.value = data.map(
            (building: Building) => building._id_building
        )
        compareChoice.value = "all"
    })

    /**
     * Comparaison en tâche de fond actuellement suivie, et son délai maximal (DEV-2199).
     */
    let comparaisonEnCours: {
        task_id: string
        delaiMax: ReturnType<typeof setTimeout>
    } | null = null

    function abandonnerComparaisonEnCours() {
        if (!comparaisonEnCours) return
        clearTimeout(comparaisonEnCours.delaiMax)
        if (taskStore.getTaskById(comparaisonEnCours.task_id)) {
            taskStore.removeTask(comparaisonEnCours.task_id)
        }
        comparaisonEnCours = null
    }

    /**
     * Récupère les indicateurs pour une variable donnée et une période donnée sur un ensemble de bâtiment
     */
    const fetchCompareGraphByBuildingIds = useFetchWrapper(
        async ({
            buildingIds,
            variable,
            periods,
            freq,
            unit,
            format,
            ranking_order,
            async,
        }: {
            buildingIds: string[]
            variable: string
            periods: string[]
            freq: "hourly" | "daily"
            unit: string
            format: string
            ranking_order: string
            async: boolean
        }) => {
            try {
                snapshotCompareBody.value = {
                    variable,
                    periods,
                    freq,
                    unit,
                    _id_buildings: buildingIds,
                }

                const result = await $getCompareGraphByBuildingIds(
                    buildingIds,
                    variable,
                    periods,
                    freq,
                    unit,
                    format,
                    ranking_order,
                    {
                        _id_user: authStore.userId,
                        async,
                    }
                )

                // Si on a un task_id alors on lance une tâche asynchrone
                if ("task_id" in result) {
                    const resultTask = result as { task_id: string }
                    // Une relance abandonne la comparaison précédente : son délai
                    // est annulé et son résultat tardif ignoré (tâche retirée).
                    abandonnerComparaisonEnCours()
                    // Délai maximal : passé ce délai, erreur nommée plutôt qu'un
                    // chargement sans fin (DEV-2199).
                    const delaiMax = setTimeout(() => {
                        comparaisonEnCours = null
                        if (!taskStore.getTaskById(resultTask.task_id)) return
                        fetchCompareGraphByBuildingIds.error.value =
                            new ServiceError(
                                {
                                    code: "calcul-trop-long",
                                    message:
                                        "Le calcul de la comparaison dépasse le délai maximal d'attente",
                                    description:
                                        "Réessayez sur une période plus courte ou avec moins de bâtiments.",
                                },
                                408
                            )
                        fetchCompareGraphByBuildingIds.isSuccess.value = false
                        taskStore.removeTask(resultTask.task_id)
                    }, DELAI_MAX_COMPARAISON_MS)
                    comparaisonEnCours = {
                        task_id: resultTask.task_id,
                        delaiMax,
                    }
                    // ajout de l'identifiant de la tâche à suivre
                    taskStore.followTask(resultTask.task_id, {
                        taskname: "get_compare_graph_by_building_ids",
                        notification: false,
                        // Si la tâche est terminée on récupère les données
                        onSuccess: ({ task_id, data }) => {
                            clearTimeout(delaiMax)
                            comparaisonEnCours = null
                            handleGraphResponse(data, variable)
                            // Supprime la tâche de la liste des tâches
                            taskStore.removeTask(task_id)
                        },
                        // Si la tâche est en erreur on met à jour le store avec l'erreur
                        onError: ({ task_id, data: { data } }) => {
                            clearTimeout(delaiMax)
                            comparaisonEnCours = null
                            fetchCompareGraphByBuildingIds.error.value =
                                new ServiceError(
                                    {
                                        code: data?.detail?.code,
                                        message: data?.detail?.message,
                                        description: data?.detail?.description,
                                    },
                                    400
                                )
                            fetchCompareGraphByBuildingIds.isSuccess.value = false
                            // Supprime la tâche de la liste des tâches
                            taskStore.removeTask(task_id)
                        },
                    })
                    return
                }

                // si on a pas les données attendues on renvoie une erreur
                if (!("graph" in result && "periods" in result)) {
                    throw new Error(
                        "Erreur lors de la récupération des données"
                    )
                }

                handleGraphResponse(result, variable)
            } catch (err) {
                // si erreur on retire les données de comparaison
                compareGraphData.value = null
                compareData.value = []
                comparePeriodIndicators.value = []
                throw err
            }
        }
    )

    function handleGraphResponse(
        result: GetCompareGraphByBuildingIdsResponse,
        variable: string
    ) {
        // si on a pas les données attendues on renvoie une erreur
        if (!("graph" in result && "periods" in result)) {
            // si erreur on retire les données de comparaison
            compareGraphData.value = null
            compareData.value = []
            comparePeriodIndicators.value = []

            return true
        }

        const { graph, periods: _periods } = result

        compareGraphData.value = graph

        const [splitVariable, stat] = variable.split(".")

        compareData.value = _periods.map((period) =>
            period.indicators
                .map((indicator) => {
                    let value = NaN

                    if (indicator[splitVariable]) {
                        value =
                            indicator[splitVariable]?.[stat as "sum" | "mean"]
                    }
                    return {
                        _id_building: indicator._id_building,
                        value,
                    }
                })
                .reduce(
                    (acc: Record<string, number>, { _id_building, value }) => {
                        acc[_id_building] = value
                        return acc
                    },
                    {}
                )
        )

        comparePeriodIndicators.value = _periods.map((period) => {
            let value = NaN

            if (period.total.data[splitVariable]) {
                value = period.total.data[splitVariable][stat as "sum" | "mean"]
            }
            return {
                date_from: period.date_from,
                date_to: period.date_to,
                value,
            }
        })
        return false
    }

    /**
     * Export les données du graphique au format CSV
     */
    const fetchExportGraphData = useFetchWrapper(async () => {
        // met moment en français
        moment.locale("fr")

        const filename = `Données comparaison ${moment().format(
            "DD-MM-YYYY HH-mm-ss"
        )}`

        // transforme le titre en nom de fichier en retirant les espaces et accents + miniuscule
        const filenameFormated = `${filename
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            // remplace les "`"
            .replace(/'/g, "")
            .replace(/\s/g, "_")
            .toLowerCase()}.xlsx`

        const variable = compareVariable.value?.split(".")?.[0] ?? ""
        const variableConfig = VARIABLES[variable]

        const maxDigit = parseInt(
            variableConfig.format?.match(/\d+/)?.[0] ?? "1"
        )

        const periods = [[...comparePeriod.value]]
        if (comparePeriodWith.value[0] && comparePeriodWith.value[1]) {
            periods.push([...comparePeriodWith.value])
        }
        periods.reverse()

        const sheetBuildingInformation = {
            sheetname: "Bâtiments",
            columns: [
                {
                    colname: "Nom",
                    values: getComparedBuildingsList.value.map(
                        (building) => building.name ?? "-"
                    ),
                },
                {
                    colname: "Adresse",
                    values: getComparedBuildingsList.value.map(
                        (building) => building.address?.inline_name ?? "-"
                    ),
                },
                {
                    colname: "Dossier",
                    values: getComparedBuildingsList.value.map(
                        (building) => building.folder?.name ?? "-"
                    ),
                },
                {
                    colname: "Nombre de compteur électrique",
                    values: getComparedBuildingsList.value.map(
                        (building) =>
                            building.sources?.filter((source: BuildingSource) =>
                                ["pdl"].includes(source.idtype)
                            )?.length ?? 0
                    ),
                },
                {
                    colname: "Nombre de compteur de gaz",
                    values: getComparedBuildingsList.value.map(
                        (building) =>
                            building.sources?.filter((source: BuildingSource) =>
                                ["pce"].includes(source.idtype)
                            )?.length ?? 0
                    ),
                },
                {
                    colname: "Nombre de capteur",
                    values: getComparedBuildingsList.value.map(
                        (building) =>
                            building.sources?.filter(
                                (source: BuildingSource) =>
                                    ![
                                        "idopenmeteo",
                                        "idmeteofrance",
                                        "pdl",
                                        "pce",
                                    ].includes(source.idtype)
                            )?.length ?? 0
                    ),
                },
                {
                    colname: "Nombre de sources",
                    values: getComparedBuildingsList.value.map(
                        (building) =>
                            building.sources?.filter(
                                (source: BuildingSource) =>
                                    !["idopenmeteo", "idmeteofrance"].includes(
                                        source.idtype
                                    )
                            )?.length ?? 0
                    ),
                },
            ],
        }

        const sheetInformation = {
            sheetname: "Indicateur par période",
            columns: [
                {
                    colname: "Période de comparaison",
                    values:
                        periods.map((dates) => {
                            const opts: Intl.DateTimeFormatOptions = {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                            }
                            return `Du ${formatDateFr(dates[0], opts)} au ${formatDateFr(dates[1], opts)}`
                        }) ?? [],
                },
                {
                    colname: "Variable",
                    values: periods.map(() => t(`variables.${variable}.long`)),
                },
                {
                    colname: "Fréquence",
                    values: periods.map(() =>
                        t(`freq.${compareFreq.value}.long`)
                    ),
                },
                {
                    colname: `Indicateur (${variableConfig?.unit})`,
                    values: comparePeriodIndicators.value.map((indicator) => {
                        return formatNumber(indicator.value, {
                            maximumFractionDigits: maxDigit,
                        })
                    }),
                },
            ],
        } as {
            sheetname: string
            columns: { colname: string; values: any[] }[]
        }

        const sheet = {
            sheetname: "Donnée par période",
            columns: [],
        } as {
            sheetname: string
            columns: { colname: string; values: any[] }[]
        }

        const graph = JSON.parse(compareGraphData.value)

        // pour chaque trace, on récupère les données et on crée une colonne
        graph.data?.forEach(
            (
                trace: {
                    name: string
                    y: any[]
                    meta: [string, { unit: string; type?: string; x: string[] }]
                },
                index: number
            ) => {
                // Pour les tracés sur les périodes de comparaison
                if (index < 2) {
                    // Ajout de la colonne des dates pour la période
                    sheet.columns.push({
                        colname: `Date ${trace.name.toLowerCase()}`,
                        values: trace.meta?.[1]?.x.map((date: string) => {
                            return moment(date).format("DD/MM/YYYY HH:mm")
                        }),
                    })
                }

                sheet.columns.push({
                    colname: `Donnée ${trace.name.toLowerCase()} (${trace.meta[1]?.unit})`,
                    // Formate les données en fonction du format de la variable
                    values: trace.y.map((value) =>
                        formatNumber(value, {
                            maximumFractionDigits: maxDigit,
                        })
                    ),
                })
            }
        )

        const sheets = [sheetBuildingInformation, sheetInformation, sheet]

        const url = await $createRapport(filenameFormated, sheets)

        window.open(url)
    })

    /**
     * Indique si une tâche est en cours
     */
    const isTaskRunning = computed(() => {
        return !!taskStore.getTaskByTaskname(
            "get_compare_graph_by_building_ids"
        )
    })

    // =================================================
    // Tags
    // =================================================

    /**
     * Récupère les tags des bâtiments de l'organisation
     */
    const fetchTags = useFetchWrapper(async () => {
        tags.value = await $getTags()
    })

    /**
     * Crée un tag pour un bâtiment
     */
    const createBuildingTag = () =>
        useFetchWrapper(
            async ({
                buildingId,
                tags,
            }: {
                buildingId: string
                tags: string[]
            }) => {
                await $createBuildingTag(buildingId, tags)
            }
        )

    /**
     * Ajoute des tags à l'entreprise et met à jour le store
     */
    const addCompanyTag = () =>
        useFetchWrapper(async ({ tagsToAdd }: { tagsToAdd: string[] }) => {
            if (!tagsToAdd.length) return

            const previousTags = [...tags.value]
            tags.value = [...new Set([...tags.value, ...tagsToAdd])]

            try {
                await $addCompanyTag(tagsToAdd)
            } catch {
                tags.value = previousTags
                throw new Error(
                    "Erreur lors de l'ajout des tags à l'entreprise"
                )
            }
        })

    /**
     *  Supprime des tags de l'entreprise et met à jour le store
     */
    const deleteCompanyTag = () =>
        useFetchWrapper(async ({ tags: tagsToDelete }: { tags: string[] }) => {
            if (!tagsToDelete?.length) return
            const previousTags = [...tags.value]
            tags.value = tags.value.filter((tag) => !tagsToDelete.includes(tag))
            try {
                await $deleteCompanyTag(tagsToDelete)
            } catch {
                tags.value = previousTags
                throw new Error(
                    "Erreur lors de la suppression des tags de l'entreprise"
                )
            }
        })
    /**
     * Supprime un tag pour un bâtiment
     */
    const deleteBuildingTag = () =>
        useFetchWrapper(
            async ({
                buildingId,
                tags,
            }: {
                buildingId: string
                tags: string[]
            }) => {
                // supprime les tags du bâtiment
                buildings.value = buildings.value.map((building) => {
                    if (building._id_building === buildingId) {
                        building.tags = building.tags.filter(
                            ({ name }) => !tags.includes(name)
                        )
                    }
                    return building
                })

                try {
                    await $deleteBuildingTag(buildingId, tags)
                } catch {
                    // remet les tags en cas d'erreur
                    buildings.value = buildings.value.map((building) => {
                        if (building._id_building === buildingId) {
                            building.tags.push(
                                ...tags.map((name) => ({ name }))
                            )
                        }
                        return building
                    })
                    throw new Error("Erreur lors de la suppression du tag")
                }
            }
        )

    /**
     * Exporte toutes les données de l'organisation dans un fichier CSV
     */
    const exportAllData = useFetchWrapper(
        async ({
            freq,
            date_from,
            date_to,
            variables,
            aggregate,
            callback,
        }: {
            freq: string
            date_from: string
            date_to: string
            variables: string[]
            aggregate: boolean
            callback?: () => void
        }) => {
            const body = {
                freq,
                date_from,
                date_to,
                variables,
                aggregate,
            } as {
                freq: string
                date_from: string
                date_to: string
                variables: string[]
                aggregate: boolean
                _id_user?: string
            }

            const headers = {
                "x-user-subscribe-id": authStore.userId,
            }

            // Si l'utilisateur n'est pas un administrateur ou owner de l'organisation, alors on ajoute son identifiant
            if (!authStore.isCompanyAdmin) {
                body._id_user = authStore.userId
            }

            const taskId = await $exportAllDataByCompanyId(
                authStore.companyId,
                body,
                headers
            )

            // ajout de l'identifiant de la tâche à suivre
            taskStore.followTask(taskId, {
                taskname: "task_export_buildings_sources",
                onReceived: () => {
                    if (callback) callback()
                },
                onError: () => {
                    if (callback) callback()
                },
            })
        }
    )

    /**
     * Exporte toutes les données de l'organisation dans un fichier CSV
     */
    const {
        fetch: fetchExportRT,
        isFetching: isFetchingExportRT,
        hasError: hasErrorExportRT,
        error: errorExportRT,
    } = useFetchWrapper(
        async ({
            date_from,
            date_to,
            aggregate,
            callback,
        }: {
            freq: string
            date_from: string
            date_to: string
            variables: string[]
            aggregate: boolean
            callback?: () => void
        }) => {
            const body = {
                date_from,
                date_to,
                aggregate,
            } as {
                date_from: string
                date_to: string
                aggregate: boolean
                _id_user?: string
            }

            const headers = {
                "x-user-subscribe-id": authStore.userId,
            }

            // Si l'utilisateur n'est pas un administrateur ou owner de l'organisation, alors on ajoute son identifiant
            if (!authStore.isCompanyAdmin) {
                body._id_user = authStore.userId
            }

            const taskId = await $exportRT(authStore.companyId, body, headers)

            // ajout de l'identifiant de la tâche à suivre
            taskStore.followTask(taskId, {
                taskname: "task_export_rt",
                onReceived: () => {
                    if (callback) callback()
                },
                onError: () => {
                    if (callback) callback()
                },
            })
        }
    )

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        folders.value = []
        compareChoice.value = "all"
        compareListId.value = []
        compareListIdByFolder.value = []
        compareFreq.value = "daily"
        comparePeriod.value = [new Date(), new Date()]
        comparePeriodWith.value = [null, null]
        buildings.value = []
        workflowEnedis.value = []
        buildingsAndFoldersCacheExpiresAt.value = 0
        tags.value = []
        compareVariable.value = "energie_tout_tout.sum"
        compareData.value = []
        compareGraphData.value = null
        comparePeriodIndicators.value = []
        snapshotCompareBody.value = null
    }

    return {
        // State
        folders,
        compareChoice,
        compareListId,
        compareListIdByFolder,
        compareFreq,
        comparePeriod,
        comparePeriodWith,
        buildings,
        workflowEnedis,
        tags,
        compareVariable,
        compareData,
        compareGraphData,
        comparePeriodIndicators,
        snapshotCompareBody,

        // Getters
        getFoldersList,
        folderTreeList,
        getComparedBuildingsList,
        getComparedFoldersList,
        getSourcesCount,
        getDisplayDataConfig,

        // Actions asynchrones
        fetchBuildingsAndFolders,
        ensureBuildingsAndFoldersForDialog,
        fetchBuildingsByCompanyId,
        fetchCompareGraphByBuildingIds,
        fetchExportGraphData,
        fetchTags,
        exportAllData,
        setBuildings,
        hydrateCompareScopeForBuilding,
        clickLink,
        createBuildingTag,
        addCompanyTag,
        deleteCompanyTag,
        deleteBuildingTag,
        fetchExportRT,

        // Reset
        $reset,

        // États de chargement
        isTaskRunning,
        isFetchingExportRT,

        // États d'erreur
        hasErrorExportRT,

        // Erreurs
        errorExportRT,
    }
})
